import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser'
import { CONVERT_API_SCOPE, COPILOT_STUDIO_SCOPE, config } from './config'

const msalConfig: Configuration = {
  auth: {
    clientId: config.clientId,
    authority: `https://login.microsoftonline.com/${config.tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
}

const scopes = [COPILOT_STUDIO_SCOPE]

export const msalInstance = new PublicClientApplication(msalConfig)

let initialized: Promise<void> | undefined

/** Initializes MSAL and completes any in-flight redirect. Safe to call repeatedly. */
export function initializeAuth(): Promise<void> {
  initialized ??= (async () => {
    await msalInstance.initialize()
    const result = await msalInstance.handleRedirectPromise()
    if (result?.account) {
      msalInstance.setActiveAccount(result.account)
    } else if (!msalInstance.getActiveAccount()) {
      const [account] = msalInstance.getAllAccounts()
      if (account) msalInstance.setActiveAccount(account)
    }
  })()
  return initialized
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount()
}

export function signIn(): Promise<void> {
  return msalInstance.loginRedirect({ scopes, prompt: 'select_account' })
}

export function signOut(): Promise<void> {
  return msalInstance.logoutRedirect({ account: msalInstance.getActiveAccount() ?? undefined })
}

/**
 * Returns an access token for the Copilot Studio direct connect endpoint,
 * falling back to an interactive redirect when consent or re-auth is needed.
 */
export async function getAgentToken(): Promise<string> {
  return acquire([COPILOT_STUDIO_SCOPE])
}

/**
 * Returns an access token for the document conversion API.
 *
 * Unlike the agent token this never redirects: conversion is a background step
 * inside a send, and navigating away mid-upload would lose the user's message.
 * Callers treat a rejection as "fall back to browser-side parsing".
 */
export async function getConvertApiToken(): Promise<string> {
  const account = msalInstance.getActiveAccount()
  if (!account) throw new Error('Not signed in.')

  const result = await msalInstance.acquireTokenSilent({ scopes: [CONVERT_API_SCOPE], account })
  return result.accessToken
}

async function acquire(scopes: string[]): Promise<string> {
  const account = msalInstance.getActiveAccount()
  if (!account) throw new Error('Not signed in.')

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account })
    return result.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes, account })
      // acquireTokenRedirect navigates away; this never resolves.
      return new Promise<string>(() => {})
    }
    throw error
  }
}
